import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, clearAuthData } from '../../utils/api';

// Access level definitions
// Level 1: Full access
// Level 2: Explore Hub, Tickets, Feedback & Bug Reports
// Level 3: Explore Hub only

const SuperAdminLayout = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [openTicketCount, setOpenTicketCount] = useState(0);
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [outreachInboxCount, setOutreachInboxCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuthAndFetchData = async () => {
      const token = localStorage.getItem('token');
      const userType = localStorage.getItem('userType');
      
      if (!token || userType !== 'super_admin') {
        navigate('/super-admin/login');
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      await fetchData();
      // Only set isInitialLoad to false AFTER user data is loaded
      setIsInitialLoad(false);
    };

    checkAuthAndFetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [userResponse, statsResponse, badgesResponse] = await Promise.all([
        axios.get(`${API}/super-admin/me`),
        axios.get(`${API}/super-admin/stats`),
        axios.get(`${API}/super-admin/sidebar-badges`).catch(() => ({ data: {} }))
      ]);
      
      setCurrentUser(userResponse.data);
      setStats(statsResponse.data);
      
      const badges = badgesResponse.data;
      setOpenTicketCount(badges.tickets || 0);
      setFeedbackCount(badges.feedback || 0);
      setOutreachInboxCount(badges.outreach_inbox || 0);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const handleLogout = () => {
    clearAuthData();
    navigate('/super-admin/login');
  };

  const menuItems = [
    { 
      key: 'dashboard', 
      label: 'Dashboard', 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      path: '/super-admin/dashboard'
    },
    {
      key: 'explore-hub',
      label: 'Explore Hub',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
      path: '/super-admin/explore-hub'
    },
    {
      key: 'organisations',
      label: 'Organisations',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      subItems: [
        { key: 'organisations-list', label: 'Organisations', path: '/super-admin/organisations' },
        { key: 'organisation-data', label: 'Organisation Data', path: '/super-admin/organisation-data' },
        { key: 'subscription-packages', label: 'Subscription Packages', path: '/super-admin/subscription-packages' },
        { key: 'flash-upgrades', label: 'Flash Upgrades', path: '/super-admin/flash-upgrades' }
      ]
    },
    {
      key: 'neo-ai',
      label: 'Neo AI',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      subItems: [
        { key: 'neo-settings', label: 'Neo Settings', path: '/super-admin/neo-settings' },
        { key: 'neo-analytics', label: 'Neo Analytics', path: '/super-admin/neo-analytics' },
        { key: 'neo-history', label: 'Neo History', path: '/super-admin/neo-history' },
        { key: 'neo-feedback', label: 'Neo Feedback', path: '/super-admin/neo-feedback' }
      ]
    },
    {
      key: 'analytics',
      label: 'Business Analytics',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      subItems: [
        { key: 'organisation-analytics', label: 'Organisation Analytics', path: '/super-admin/organisation-analytics' },
        { key: 'revenue-payment-metrics', label: 'Revenue & Payment Metrics', path: '/super-admin/business-analytics' },
        { key: 'subscriptions', label: 'Subscriptions', path: '/super-admin/subscriptions-analytics' },
        { key: 'financial-health', label: 'Financial Health', path: '/super-admin/financial-health' },
        { key: 'geography-demographics', label: 'Geography & Demographics', path: '/super-admin/geography-demographics' },
        { key: 'time-based-insights', label: 'Time-Based Insights', path: '/super-admin/time-based-insights' }
      ]
    },
    {
      key: 'app-analytics',
      label: 'App Analytics (Combined)',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      subItems: [
        { key: 'notification-analytics', label: 'Notification Analytics', path: '/super-admin/notification-analytics' },
        { key: 'explore-analytics', label: 'Explore Analytics', path: '/super-admin/app-analytics/explore-analytics' }
      ]
    },
    {
      key: 'team-app-analytics',
      label: 'Team App Analytics',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      subItems: [
        { key: 'user-engagement', label: 'User Engagement Metrics', path: '/super-admin/app-analytics/user-engagement' },
        { key: 'feature-usage', label: 'Feature Usage Metrics', path: '/super-admin/app-analytics/feature-usage' },
        { key: 'user-behavior', label: 'User Behaviour & Journey Metrics', path: '/super-admin/app-analytics/user-behaviour' },
        { key: 'performance-technical', label: 'Performance & Technical Metrics', path: '/super-admin/app-analytics/performance-technical' },
        { key: 'retention-lifecycle', label: 'Retention & Lifecycle Metrics', path: '/super-admin/app-analytics/retention-lifecycle' }
      ]
    },
    {
      key: 'admin-portal-analytics',
      label: 'Admin App + Web Portal Analytics',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      subItems: [
        { key: 'usage-feature-adoption', label: 'Usage & Feature Adoption', path: '/super-admin/admin-portal/usage-adoption' },
        { key: 'workflow-productivity', label: 'Workflow & Productivity', path: '/super-admin/admin-portal/workflow-productivity' },
        { key: 'access-security', label: 'Access & Security', path: '/super-admin/admin-portal/access-security' },
        { key: 'error-support', label: 'Error & Support Tracking', path: '/super-admin/admin-portal/error-support' }
      ]
    },
    {
      key: 'help-support',
      label: 'Help & Support',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      subItems: [
        { key: 'tickets', label: 'Tickets', path: '/super-admin/help/tickets', badgeCount: openTicketCount },
        { key: 'broadcast', label: 'Broadcast', path: '/super-admin/help/broadcast' }
      ]
    },
    {
      key: 'tools-settings',
      label: 'Tools & Settings',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      subItems: [
        { key: 'settings-users', label: 'Users', path: '/super-admin/settings/users' },
        { key: 'app-metadata', label: 'App Metadata', path: '/super-admin/app-metadata' },
        { key: 'faq-management', label: 'FAQ Management', path: '/super-admin/faq-management' },
        { key: 'feedback-bugs', label: 'Feedback & Bug Reports', path: '/super-admin/feedback-bugs', badgeCount: feedbackCount },
        { key: 'deployment-tools', label: 'Deployment Tools', path: '/super-admin/deployment-tools' },
        { key: 'paypal-setup', label: 'PayPal Setup', path: '/super-admin/paypal-setup' },
        { key: 'paypal-events', label: 'PayPal Events', path: '/super-admin/paypal-events' },
        { key: 'platform-logs', label: 'Platform Logs', path: '/super-admin/platform-logs' },
        { key: 'system-logs', label: 'System Logs', path: '/super-admin/system-logs' },
        { key: 'marketing-campaigns', label: 'Marketing Campaigns', path: '/super-admin/marketing-campaigns', badgeCount: outreachInboxCount }
      ]
    }
  ];

  // Filter menu items based on user's access level
  const filteredMenuItems = useMemo(() => {
    const accessLevel = currentUser?.access_level || 1;
    
    // Level 1: Full access - return all items
    if (accessLevel === 1) {
      return menuItems;
    }
    
    // Level 2: Explore Hub, Tickets, Feedback & Bug Reports
    if (accessLevel === 2) {
      const allowedItems = [];
      
      // Add Explore Hub
      const exploreHub = menuItems.find(item => item.key === 'explore-hub');
      if (exploreHub) allowedItems.push(exploreHub);
      
      // Add Help & Support with only Tickets subitem
      const helpSupport = menuItems.find(item => item.key === 'help-support');
      if (helpSupport) {
        const ticketsOnly = {
          ...helpSupport,
          subItems: helpSupport.subItems.filter(sub => sub.key === 'tickets')
        };
        if (ticketsOnly.subItems.length > 0) allowedItems.push(ticketsOnly);
      }
      
      // Add Tools & Settings with only Feedback & Bug Reports subitem
      const toolsSettings = menuItems.find(item => item.key === 'tools-settings');
      if (toolsSettings) {
        const feedbackOnly = {
          ...toolsSettings,
          subItems: toolsSettings.subItems.filter(sub => sub.key === 'feedback-bugs')
        };
        if (feedbackOnly.subItems.length > 0) allowedItems.push(feedbackOnly);
      }
      
      return allowedItems;
    }
    
    // Level 3: Explore Hub only
    if (accessLevel === 3) {
      const exploreHub = menuItems.find(item => item.key === 'explore-hub');
      return exploreHub ? [exploreHub] : [];
    }
    
    return menuItems;
  }, [currentUser?.access_level, menuItems, openTicketCount]);

  // Get all allowed paths for the current user's access level
  const getAllowedPaths = useMemo(() => {
    const paths = [];
    filteredMenuItems.forEach(item => {
      if (item.path) paths.push(item.path);
      if (item.subItems) {
        item.subItems.forEach(sub => paths.push(sub.path));
      }
    });
    return paths;
  }, [filteredMenuItems]);

  // Get the default landing page based on access level
  const getDefaultLandingPage = () => {
    const accessLevel = currentUser?.access_level || 1;
    if (accessLevel === 1) {
      return '/super-admin/dashboard';
    }
    // Level 2 and 3 land on Explore Hub
    return '/super-admin/explore-hub';
  };

  // Check if current path is allowed
  const isCurrentPathAllowed = useMemo(() => {
    if (!currentUser || isInitialLoad) return true; // Don't block during load
    const currentPath = location.pathname;
    return getAllowedPaths.some(path => currentPath === path || currentPath.startsWith(path + '/'));
  }, [currentUser, location.pathname, getAllowedPaths, isInitialLoad]);

  // Redirect if path is not allowed
  useEffect(() => {
    if (!currentUser || isInitialLoad) return;
    
    if (!isCurrentPathAllowed) {
      const defaultPage = getDefaultLandingPage();
      navigate(defaultPage, { replace: true });
    }
  }, [currentUser, isCurrentPathAllowed, navigate, isInitialLoad]);

  const isActiveItem = (item) => {
    if (item.path) {
      return location.pathname === item.path;
    }
    if (item.subItems) {
      return item.subItems.some(subItem => location.pathname === subItem.path);
    }
    return false;
  };

  const isActiveSubItem = (subItem) => {
    return location.pathname === subItem.path;
  };

  // Show loading state while checking access OR if path is not allowed (redirect in progress)
  if (isInitialLoad || (!isCurrentPathAllowed && currentUser)) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className="mt-2 text-gray-600">Loading super admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50 flex">
      {/* Sidebar */}
      <div className="w-80 bg-gradient-to-b from-gray-900 via-black to-gray-900 border-r border-gray-800 flex flex-col h-screen shadow-2xl relative z-50">
        {/* Header */}
        <div className="p-4 bg-gray-800 rounded-lg m-3 mb-2 border border-purple-600/30">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold text-sm">SiteOrbit</h3>
              <p className="text-purple-300 text-xs mt-0.5">Super Admin Dashboard</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 hover:border-white/30 transition-all duration-200 group"
              title="Sign Out"
            >
              <svg className="w-5 h-5 text-white group-hover:text-red-300 transition-colors" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-2 relative z-50">
          <div className="space-y-1">
            {filteredMenuItems.map((item) => (
              <div key={item.key}>
                {/* Main Menu Item */}
                {item.subItems ? (
                  /* Parent menu with submenus - not clickable */
                  <div className={`w-full text-left px-4 py-3 text-sm font-semibold rounded-lg ${
                    isActiveItem(item)
                      ? 'bg-gradient-to-r from-purple-600 to-purple-500 text-white shadow-lg'
                      : 'text-gray-300'
                  }`}>
                    <div className="flex items-center space-x-3">
                      {item.icon}
                      <span>{item.label}</span>
                    </div>
                  </div>
                ) : (
                  /* Regular menu item - clickable */
                  <button
                    onClick={() => navigate(item.path)}
                    className={`w-full text-left px-4 py-3 text-sm font-semibold rounded-lg transition-all duration-200 ${
                      isActiveItem(item)
                        ? 'bg-gradient-to-r from-purple-600 to-purple-500 text-white shadow-lg'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {item.icon}
                      <span>{item.label}</span>
                    </div>
                  </button>
                )}

                {/* Submenu Items - Always Visible */}
                {item.subItems && (
                  <div className="ml-4 mt-1 space-y-1">
                    {item.subItems.map((subItem) => (
                      <button
                        key={subItem.key}
                        onClick={() => navigate(subItem.path)}
                        className={`w-full text-left px-4 py-2 text-sm rounded-lg transition-all duration-200 ${
                          isActiveSubItem(subItem)
                            ? 'bg-gray-800 text-purple-300 font-medium'
                            : 'text-gray-400 hover:bg-gray-800 hover:text-purple-300'
                        }`}
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center space-x-3">
                            <svg className="w-2 h-2" fill="currentColor" viewBox="0 0 8 8">
                              <circle cx="4" cy="4" r="3" />
                            </svg>
                            <span>{subItem.label}</span>
                          </div>
                          {subItem.badge && (
                            <span className="px-2 py-0.5 bg-yellow-500 text-yellow-900 text-xs font-bold rounded-full">
                              {subItem.badge}
                            </span>
                          )}
                          {subItem.badgeCount != null && subItem.badgeCount > 0 && (
                            <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] text-center">
                              {subItem.badgeCount > 99 ? '99+' : subItem.badgeCount}
                            </span>
                          )}
                          {subItem.badgeCount != null && subItem.badgeCount === 0 && (
                            <span className="px-2 py-0.5 bg-gray-200 text-gray-500 text-xs font-bold rounded-full min-w-[20px] text-center">
                              0
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </nav>

        {/* User Profile Card */}
        {currentUser && (
          <div className="p-4 border-t border-gray-700 bg-gray-800">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {currentUser.profile_photo_url ? (
                  <img
                    src={currentUser.profile_photo_url}
                    alt={`${currentUser.first_name} ${currentUser.last_name}`}
                    className="w-12 h-12 rounded-full object-cover border-2 border-purple-500"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center border-2 border-purple-500">
                    <span className="text-white font-semibold text-lg">
                      {(currentUser.first_name?.[0] || '') + (currentUser.last_name?.[0] || '')}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-sm truncate">
                  {`${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'Super Admin'}
                </p>
                <div className="flex items-center space-x-2">
                  <p className="text-purple-300 text-xs truncate">
                    @{currentUser.username || 'superadmin'}
                  </p>
                  <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                    currentUser.access_level === 1 
                      ? 'bg-green-500/20 text-green-300' 
                      : currentUser.access_level === 2 
                        ? 'bg-yellow-500/20 text-yellow-300'
                        : 'bg-gray-500/20 text-gray-300'
                  }`}>
                    L{currentUser.access_level || 1}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-screen">
        <div className="flex-1 overflow-y-auto p-8">
          <Outlet context={{ currentUser, stats, fetchData }} />
        </div>
      </div>
    </div>
  );
};

export default SuperAdminLayout;
